/* battleEventQueue — deterministic parser turning the SERVER-authoritative
 * `battle.rounds[]` array into a flat, typed animation queue that the
 * frontend controller can play through with pacing / speed controls.
 *
 * CRITICAL: this module NEVER calculates a winner, HP, crit, miss or
 * status. It only interprets the pre-computed server payload:
 *   - round.log[]       — human-readable narrative strings
 *   - round.hits[]      — structured {kind, target_side, value, crit}
 *   - round.hp_after    — post-round HP (authoritative)
 *   - round.hp_max      — max HP per side
 *   - round.status_after — status list per side at end of round
 *   - round.winner      — 'challenger' | 'opponent'
 *
 * Output events (each has {type, ...payload, duration_ms}):
 *   battle_start
 *   round_start        {round, is_final}
 *   log                {text}
 *   ability            {side, name, target_name}
 *   attack             {side}
 *   miss               {side}
 *   damage             {target_side, value, crit, hp_after_target, hp_max}
 *   status_applied     {target_side, code, name}
 *   round_end          {round, winner_side, round_wins, hp_after, hp_max}
 *   momentum_shift     {toward_side}
 *   critical_callout   {kind: 'first-strike'|'critical'|'comeback'|'momentum'|'weather-advantage'|'final-stand', text}
 *   battle_end         {winner_side}
 */

const RE_ROUND_HEADER = /^—\s*Round\s+(\d+)\s*—/i;
const RE_ROUND_WIN    = /^—\s*(.+?)\s+takes the round/i;
const RE_ABILITY      = /^(.+?)\s+unleashes\s+(.+?)!?$/i;
const RE_MISS         = /goes wide|attack (?:misses|whiffs)/i;
const RE_STATUS_HIT   = /is (?:shaken by|burning|poisoned|frozen|stunned|bleeding|rooted|blinded|confused)/i;

// Approx display time per event at 1× speed. Tuned so a typical 3-round
// battle takes ~15-25 seconds to watch and never feels sluggish.
const D = {
  battle_start: 250,
  round_start: 900,
  log: 550,
  ability: 850,
  attack: 400,
  miss: 500,
  damage: 550,
  status_applied: 500,
  round_end: 1200,
  momentum_shift: 400,
  critical_callout: 900,
  battle_end: 0,
};

function pushDamageEventsFromHits(events, round, chName, opName) {
  const hits = round.hits || [];
  const hpMax = round.hp_max || {};
  const hpAfter = round.hp_after || {};
  // Log lines don't always match damage 1:1, so we place structured
  // damage events after the ability/attack line block for each hit.
  hits.forEach((h) => {
    if (h.kind === 'damage') {
      events.push({
        type: 'damage',
        target_side: h.target_side,
        value: Math.abs(h.value || 0),
        crit: !!h.crit,
        hp_after_target: hpAfter[h.target_side] ?? null,
        hp_max: hpMax[h.target_side] ?? null,
        duration_ms: D.damage,
      });
      if (h.crit) {
        events.push({
          type: 'critical_callout',
          kind: 'critical',
          text: 'CRITICAL',
          duration_ms: D.critical_callout,
        });
      }
    } else if (h.kind === 'miss') {
      events.push({ type: 'miss', side: h.target_side === 'challenger' ? 'opponent' : 'challenger', duration_ms: D.miss });
    } else if (h.kind === 'heal') {
      // No-op for now — heal is expressed via hp_after; we could animate a +N later.
    }
  });
}

export function buildEventQueue(battle) {
  const events = [];
  if (!battle || !Array.isArray(battle.rounds)) return events;

  const chName = battle.challenger_snapshot?.name || 'Challenger';
  const opName = battle.opponent_snapshot?.name || 'Opponent';
  const bestOf = battle.best_of || 5;
  const roundsToWin = Math.floor(bestOf / 2) + 1;

  events.push({ type: 'battle_start', duration_ms: D.battle_start });

  let runningWins = { challenger: 0, opponent: 0 };
  let previousWinner = null;

  battle.rounds.forEach((r, idx) => {
    const roundNum = r.round || idx + 1;
    const willBeFinal = (
      runningWins.challenger === roundsToWin - 1 ||
      runningWins.opponent === roundsToWin - 1
    );
    events.push({
      type: 'round_start',
      round: roundNum,
      is_final: willBeFinal && idx > 0,
      duration_ms: D.round_start,
    });

    // Walk the log, emitting typed events for recognisable lines.
    let awaitingAttack = null;   // side that just unleashed an ability
    (r.log || []).forEach((line) => {
      const s = String(line || '').trim();
      if (!s) return;

      // Round header — already emitted as round_start, skip.
      if (RE_ROUND_HEADER.test(s)) return;

      // Round win footer — becomes round_end below.
      if (RE_ROUND_WIN.test(s)) return;

      const abilityMatch = s.match(RE_ABILITY);
      if (abilityMatch) {
        const [, actor, ability] = abilityMatch;
        const side = actor.trim() === chName ? 'challenger' : 'opponent';
        events.push({
          type: 'ability',
          side,
          actor,
          name: ability.replace(/[!?.]$/, '').trim(),
          duration_ms: D.ability,
        });
        awaitingAttack = side;
        return;
      }
      if (RE_MISS.test(s)) {
        events.push({ type: 'miss', side: awaitingAttack || 'challenger', duration_ms: D.miss });
        awaitingAttack = null;
        return;
      }
      if (RE_STATUS_HIT.test(s)) {
        // We'll pull the full status list from status_after in round_end
        // — meanwhile just fade in this narrative line.
        events.push({ type: 'log', text: s, duration_ms: D.log });
        return;
      }
      // Everything else is narrative flavour.
      events.push({ type: 'log', text: s, duration_ms: D.log });
    });

    // Slot in structured damage / miss / crit events derived from hits[].
    pushDamageEventsFromHits(events, r, chName, opName);

    // Apply status_after — one event per new status.
    const sa = r.status_after || {};
    ['challenger', 'opponent'].forEach((side) => {
      (sa[side] || []).forEach((st) => {
        events.push({
          type: 'status_applied',
          target_side: side,
          code: st.code,
          name: st.name || st.code,
          duration_ms: D.status_applied,
        });
      });
    });

    // Comeback callout — losing side wins a round after losing the previous.
    if (previousWinner && previousWinner !== r.winner && idx >= 1) {
      events.push({
        type: 'critical_callout',
        kind: 'comeback',
        text: 'COMEBACK',
        duration_ms: D.critical_callout,
      });
    }

    if (r.winner === 'challenger') runningWins.challenger += 1;
    else runningWins.opponent += 1;

    events.push({
      type: 'round_end',
      round: roundNum,
      winner_side: r.winner,
      round_wins: { ...runningWins },
      hp_after: r.hp_after,
      hp_max: r.hp_max,
      duration_ms: D.round_end,
    });

    previousWinner = r.winner;
  });

  events.push({
    type: 'battle_end',
    winner_side: battle.winner,
    round_wins: battle.round_wins,
    duration_ms: 0,
  });

  return events;
}
